import {
  useMarketAccountsContext,
  useSecuritiesContext,
  useSecurity,
  useUnifiedLiquidityContext,
  type MarketDataCardConfig,
} from '@talos/kyoko';
import { useEffect, useMemo, useRef } from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { MarketSelectorItem } from '../MarketSelector/types';
import { getMarketDataCardConfigAction } from './getMarketDataCardConfigAction';

interface UseInitializedMarketAccountsConfigParams {
  config: MarketDataCardConfig;
  onSave: (config: MarketDataCardConfig) => void;
  onDelete: (config: MarketDataCardConfig) => void;
  items: MarketSelectorItem[];
}

export function useInitializedMarketAccountsConfig({
  config,
  onSave,
  onDelete,
  items,
}: UseInitializedMarketAccountsConfigParams): MarketDataCardConfig {
  const { enableUnifiedLiquidityMarketSymbols } = useFeatureFlag();
  const security = useSecurity(config.symbol);
  const { marketAccountsByName } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { isLoaded: unifiedLiquidityTokensLoaded, unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const lastActionKeyRef = useRef<string>();

  const configAction = useMemo(
    () =>
      getMarketDataCardConfigAction({
        config,
        enableUnifiedLiquidityMarketSymbols,
        items,
        hasSecurity: security != null,
        marketAccountsByName,
        securitiesBySymbol,
        unifiedLiquidityTokenBySymbol,
        unifiedLiquidityTokensLoaded,
      }),
    [
      config,
      enableUnifiedLiquidityMarketSymbols,
      items,
      marketAccountsByName,
      securitiesBySymbol,
      security,
      unifiedLiquidityTokenBySymbol,
      unifiedLiquidityTokensLoaded,
    ]
  );

  useEffect(() => {
    if (!configAction) {
      return;
    }

    const actionKey =
      configAction.type === 'remove'
        ? `remove:${config.id}:${config.symbol ?? ''}`
        : `save:${JSON.stringify(configAction.config)}`;
    if (lastActionKeyRef.current === actionKey) {
      return;
    }
    lastActionKeyRef.current = actionKey;

    if (configAction.type === 'remove') {
      onDelete(config);
      return;
    }

    onSave(configAction.config);
  }, [config, configAction, onDelete, onSave]);

  return configAction?.type === 'save' ? configAction.config : config;
}
